/**
 * Permission utility functions for role-based access control
 */

/**
 * Check if user has a specific permission
 * @param {Object} user - User object from AuthContext
 * @param {string} permissionPath - Dot-notation permission path (e.g., 'website.photos')
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (user, permissionPath) => {
  if (!user) {
    console.log(`❌ hasPermission('${permissionPath}'): No user`);
    return false;
  }
  
  // Admin always has full access
  if (user.role === 'admin') {
    console.log(`✅ hasPermission('${permissionPath}'): User is admin`);
    return true;
  }
  
  // Check if user has custom role with permissions
  if (user.has_custom_role && user.permissions) {
    const pathParts = permissionPath.split('.');
    let current = user.permissions;
    
    console.log(`🔍 hasPermission('${permissionPath}'): Checking custom role`, {
      has_custom_role: user.has_custom_role,
      permissions: user.permissions
    });
    
    for (const part of pathParts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
        console.log(`  ✅ Found '${part}':`, current);
      } else {
        console.log(`  ❌ NOT found '${part}' in:`, current);
        return false;
      }
    }
    
    const result = current === true;
    console.log(`  Final result for '${permissionPath}': ${result} (value=${current})`);
    return result;
  }
  
  // No custom role - allow access (backward compatibility)
  console.log(`✅ hasPermission('${permissionPath}'): No custom role, backward compatibility`);
  return true;
};

/**
 * Check if user has any of the specified base roles
 * @param {Object} user - User object from AuthContext
 * @param {Array<string>} roles - Array of role names to check
 * @returns {boolean} - True if user has any of the roles
 */
export const hasAnyRole = (user, roles) => {
  if (!user) return false;
  
  // Check custom role name
  if (user.role_name && roles.includes(user.role_name.toLowerCase())) {
    return true;
  }
  
  // Check base role
  return roles.includes(user.role);
};

/**
 * Check if user should have access to a page based on permission or role
 * @param {Object} user - User object from AuthContext
 * @param {string} permissionPath - Permission path to check (e.g., 'website.photos')
 * @param {Array<string>} fallbackRoles - Roles to allow if no custom permissions (backward compatibility)
 * @returns {boolean} - True if user has access
 */
export const hasPageAccess = (user, permissionPath, fallbackRoles = []) => {
  if (!user) return false;
  
  // Admin always has access
  if (user.role === 'admin') return true;
  
  // Check permission if user has custom role
  if (user.has_custom_role) {
    return hasPermission(user, permissionPath);
  }
  
  // Backward compatibility - check fallback roles
  return hasAnyRole(user, fallbackRoles);
};
